import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useTranslations } from 'hooks/useTranslations';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { useLang } from 'models/langContext';
import { Box } from '@mui/system';
import { StyledPageTitle } from 'theme/styles';
import { Grid, Stack, Typography } from '@mui/material';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import {
  AccessoryInformationContainer,
  AccessoryInformationTitle,
} from 'pages/AncillariesPage/AccessoriesPage/DetailAccessories/styles';
import { Chips } from 'components/atoms/Chips';
import { EAccessoryLabels } from 'pages/AncillariesPage/AccessoriesPage/DetailAccessories';
import {
  getFieldsByAccessoriesType,
  getFieldsByAmmunitionType,
  getFieldsByComponentType,
} from 'pages/AncillariesPage/helpers';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import {
  EArtAmmunitionType,
  EArtAncillaryTypeName,
  EArtAncillaryTypeNameLowerCase,
  EArtComponentType,
} from 'constants/ArtAccessoryType';
import { disableAncillary } from 'requests/ancillaries';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import {
  AmmunitionInformationContainer,
  AmmunitionInformationTitle,
} from 'pages/AncillariesPage/AmmunitionPage/DetailAmmunition/styles';
import { formatDate } from 'utils/date';
import { EAmmunitionLabels } from 'pages/AncillariesPage/AmmunitionPage/DetailAmmunition';
import { useGlobalProperty } from 'models/globalPropertyContext';
import {
  ComponentInformationContainer,
  ComponentInformationTitle,
} from 'pages/AncillariesPage/ComponentsPage/DetailComponents/styles';
import { EComponentLabels } from 'pages/AncillariesPage/ComponentsPage/DetailComponents';
import Link from '@mui/material/Link';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { useDictionary } from 'models/dictionaryContext';
import { findInDictionary } from 'helpers/dictionary';

const DisableAncillaryPage = () => {
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { setAlert } = useAlert();
  const { hasPermissions } = usePermission();
  const { shortDateFormat } = useGlobalProperty();
  const { id = '', type = '' } = useParams<{ id: string; type: string }>();
  const {
    accessoryDetail,
    ammunitionDetail,
    componentDetail,
    getAccessoryDetail,
    getAmmunitionDetail,
    getComponentDetail,
  } = useAncillaries();
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const [disableButtons, setDisableButtons] = useState(false);
  const navigate = useNavigate();
  const {
    getMake,
    make,
    getModel,
    model,
    getFirearmType,
    firearmType,
    getManufacturer,
    manufacturer,
  } = useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getMake();
      getModel();
      getManufacturer();
      getFirearmType();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async () => {
      if (id && type) {
        if (type === EArtAncillaryTypeNameLowerCase.ACCESSORY) {
          await getAccessoryDetail(id);
        } else if (type === EArtAncillaryTypeNameLowerCase.AMMUNITION) {
          await getAmmunitionDetail(id);
        } else if (type === EArtAncillaryTypeNameLowerCase.COMPONENTS) {
          await getComponentDetail(id);
        }
      }
    })();
  }, [id, selectedLanguage]);

  useEffect(() => {
    let fields;
    if (accessoryDetail && accessoryDetail.typeUniqueId) {
      fields = getFieldsByAccessoriesType(accessoryDetail.typeUniqueId);
    } else if (ammunitionDetail && ammunitionDetail.typeUniqueId) {
      fields = getFieldsByAmmunitionType(ammunitionDetail.typeUniqueId as EArtAmmunitionType);
    } else if (componentDetail && componentDetail.typeUniqueId) {
      fields = getFieldsByComponentType(componentDetail.typeUniqueId as EArtComponentType);
    }

    setAccessFields(fields || []);
  }, [accessoryDetail, ammunitionDetail, componentDetail]);

  const showField = (fieldName: string) => accessFields.includes(fieldName);

  const getReturnUrl = () => {
    if (accessoryDetail) {
      return ERouteLinks.ViewAccessories.replace(':id', id);
    }
    if (ammunitionDetail) {
      return ERouteLinks.ViewAmmunition.replace(':id', id);
    }
    if (componentDetail) {
      return ERouteLinks.ViewComponents.replace(':id', id);
    }
    return '';
  };

  const handleDisable = async () => {
    setDisableButtons(true);
    await disableAncillary(id);
    setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
      text: 'Ancillary has been disabled successfully.',
      variant: EAlertVariants.success,
    });
    navigate(getReturnUrl());
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.ancillaries.name'), route: ERouteLinks.Ancillaries },
          {
            label: t('ancillary.details.header'),
            route: ERouteLinks.ViewAncillary.replace(':id', id).replace(':type', type),
          },
          t('ancillary.disable.header'),
        ]}
      />
      <Box sx={{ marginBottom: '20px' }}>
        <Box sx={{ mb: 1 }}>
          <StyledPageTitle variant="h4">{t('ancillary.details.header')}</StyledPageTitle>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="body2">{t('ancillary.disable.usageInfo.text')}</Typography>
        </Box>

        <Box>
          <Alert
            text={t('ancillary.disable.confirmation.text')}
            variant={EAlertVariants.warning}
          />
        </Box>
      </Box>

      {accessoryDetail && (
        <DetailsTemplate title={accessoryDetail?.type} pageType={EDetailsPageTypes.view}>
          <AccessoryInformationContainer size={{ xs: 12 }} container>
            <Grid size={{ xs: 12 }}>
              <Stack>
                <AccessoryInformationTitle variant="subtitle1">
                  {t(EAccessoryLabels.accessoryGeneralInformation)}
                </AccessoryInformationTitle>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EAccessoryLabels.accessoryType)}
                </Typography>
                <Typography variant="body2">{accessoryDetail?.type}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography
                  variant="subtitle2"
                  sx={{ marginBottom: '4px', textTransform: 'uppercase' }}
                >
                  {t(EAccessoryLabels.artId)}
                </Typography>
                <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                  {accessoryDetail?.artId}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Stack>
                <Link
                  href={`${ERouteLinks.ViewAncillaries.replace(
                    ':id',
                    accessoryDetail.artUniqueId,
                  ).replace(':type', EArtAncillaryTypeName.ACCESSORY.toLowerCase())}`}
                  underline="none"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    fullWidth
                    label={t('accessoryDetails.showArtInformation.button')}
                    variant={EButtonVariants.contained}
                    size={EButtonSizes.small}
                  />
                </Link>
              </Stack>
            </Grid>
            {showField('manufacturer') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.manufacturer)}
                  </Typography>
                  <Typography variant="body2">
                    {accessoryDetail?.manufacturer ?? '-'}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {showField('calibre') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.calibre)}
                  </Typography>
                  <Typography variant="body2">{accessoryDetail?.calibre ?? '-'}</Typography>
                </Stack>
              </Grid>
            )}
            {showField('hostType') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.hostType)}
                  </Typography>
                  <Typography variant="body2">{accessoryDetail?.hostType ?? '-'}</Typography>
                </Stack>
              </Grid>
            )}
            {showField('proofHouse') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.proofHouse)}
                  </Typography>
                  <Typography variant="body2">{accessoryDetail?.proofHouse ?? '-'}</Typography>
                </Stack>
              </Grid>
            )}
            {showField('composition') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.composition)}
                  </Typography>
                  <Typography variant="body2">
                    {accessoryDetail?.composition ?? '-'}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {showField('magnification') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.magnification)}
                  </Typography>
                  <Typography variant="body2">
                    {accessoryDetail?.magnification ?? '-'}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {showField('utility') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.utility)}
                  </Typography>
                  <Typography variant="body2">{accessoryDetail?.utility ?? '-'}</Typography>
                </Stack>
              </Grid>
            )}
            {showField('reticle') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.reticle)}
                  </Typography>
                  <Typography variant="body2">{accessoryDetail?.reticle ?? '-'}</Typography>
                </Stack>
              </Grid>
            )}
            {showField('lightcolour') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.lightcolour)}
                  </Typography>
                  <Typography variant="body2">
                    {accessoryDetail?.lightcolour ?? '-'}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {showField('capacity') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAccessoryLabels.capacity)}
                  </Typography>
                  <Typography variant="body2">{accessoryDetail?.capacity ?? '-'}</Typography>
                </Stack>
              </Grid>
            )}
          </AccessoryInformationContainer>
          <AccessoryInformationContainer size={{ xs: 12 }} container borderHidden>
            <Grid size={{ xs: 12 }}>
              <Stack>
                <AccessoryInformationTitle variant="subtitle1">
                  {t(EAccessoryLabels.accessoryDetailedInformation)}
                </AccessoryInformationTitle>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EAccessoryLabels.firecycleID)}
                </Typography>
                <Typography variant="body2">{accessoryDetail?.fireCycleId ?? '-'}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EAccessoryLabels.serialNumber)}
                </Typography>
                <Typography variant="body2">{accessoryDetail?.serialNumber ?? '-'}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EAccessoryLabels.componentState)}
                </Typography>
                <Typography variant="body2">
                  <Chips
                    text={emptyFieldHolder(accessoryDetail?.state)}
                    variant={
                      renderDataColorByValues(
                        accessoryDetail.activityUniqueId,
                        accessoryDetail.legalityUniqueId,
                        accessoryDetail.stateUniqueId,
                      ).stateField
                    }
                  />
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EAccessoryLabels.exhibitNumber)}
                </Typography>
                <Typography variant="body2">
                  {accessoryDetail?.exhibitNumber ?? '-'}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EAccessoryLabels.currentOwner)}
                </Typography>
                <Typography variant="body2">{accessoryDetail?.ownerName ?? '-'}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EAccessoryLabels.currentKeeper)}
                </Typography>
                <Typography variant="body2">{accessoryDetail?.keeperName ?? '-'}</Typography>
              </Stack>
            </Grid>
          </AccessoryInformationContainer>
        </DetailsTemplate>
      )}

      {ammunitionDetail && (
        <DetailsTemplate title={ammunitionDetail?.type} pageType={EDetailsPageTypes.view}>
          <>
            <AmmunitionInformationContainer size={{ xs: 12 }} container>
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <AmmunitionInformationTitle variant="subtitle1">
                    {t(EAmmunitionLabels.ammunitionGeneralInformation)}
                  </AmmunitionInformationTitle>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAmmunitionLabels.ammunitionType)}
                  </Typography>
                  <Typography variant="body2">{ammunitionDetail?.type}</Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography
                    variant="subtitle2"
                    sx={{ marginBottom: '4px', textTransform: 'uppercase' }}
                  >
                    {t(EAmmunitionLabels.artId)}
                  </Typography>
                  <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                    {ammunitionDetail?.artId}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Stack>
                  <Link
                    href={`${ERouteLinks.ViewAncillaries.replace(
                      ':id',
                      ammunitionDetail.artUniqueId,
                    ).replace(':type', EArtAncillaryTypeName.AMMUNITION.toLowerCase())}`}
                    underline="none"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      fullWidth
                      label={t('ammunitionDetails.showArtInformation.button')}
                      variant={EButtonVariants.contained}
                      size={EButtonSizes.small}
                    />
                  </Link>
                </Stack>
              </Grid>

              {showField('productionDate') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.productionDate)}
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(ammunitionDetail?.productionDate, shortDateFormat)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('manufacturer') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.ammunitionManufacturer)}
                    </Typography>
                    <Typography variant="body2">{ammunitionDetail?.manufacturer}</Typography>
                  </Stack>
                </Grid>
              )}
              {showField('factory') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.factory)}
                    </Typography>
                    <Typography variant="body2">{ammunitionDetail?.factory}</Typography>
                  </Stack>
                </Grid>
              )}
              {showField('calibreGauge') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.calibreGauge)}
                    </Typography>
                    <Typography variant="body2">{ammunitionDetail?.calibre}</Typography>
                  </Stack>
                </Grid>
              )}
              {showField('functionalType') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.functionalType)}
                    </Typography>
                    <Typography variant="body2">{ammunitionDetail?.functionalType}</Typography>
                  </Stack>
                </Grid>
              )}
              {showField('cartridgeComposition') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.cartridgeComposition)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetail?.cartridgeComposition}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('colourOfPrimerSealant') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.colourOfPrimerSealant)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetail?.colourOfPrimerSealant}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('projectileShape') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.projectileShape)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetail?.projectileShape}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('headstampYear') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.headstampYear)}
                    </Typography>
                    <Typography variant="body2">{ammunitionDetail?.headstampYear}</Typography>
                  </Stack>
                </Grid>
              )}
              {showField('alphanumericalMarkins') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.alphanumericalMarkins)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetail?.alphanumericalMarkins}
                    </Typography>
                  </Stack>
                </Grid>
              )}
            </AmmunitionInformationContainer>
            <AmmunitionInformationContainer size={{ xs: 12 }} container borderHidden>
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <AmmunitionInformationTitle variant="subtitle1">
                    {t(EAmmunitionLabels.ammunitionDetailedInformation)}
                  </AmmunitionInformationTitle>
                </Stack>
              </Grid>
              {showField('fireCycleId') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.firecycleID)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetail?.fireCycleId ?? '-'}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('lotNumber') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.lotNumber)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetail?.lotNumber ?? '-'}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('batchNumber') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.batchNumber)}
                    </Typography>
                    <Typography variant="body2">
                      {accessoryDetail?.batchNumber ?? '-'}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('state') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.ammunitionState)}
                    </Typography>
                    <Typography variant="body2">
                      <Chips
                        text={emptyFieldHolder(ammunitionDetail?.state)}
                        variant={
                          renderDataColorByValues(
                            ammunitionDetail.activityUniqueId,
                            ammunitionDetail.legalityUniqueId,
                            ammunitionDetail.stateUniqueId,
                          ).stateField
                        }
                      />
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('ownerName') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.currentOwner)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetail?.ownerName ?? '-'}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('keeperName') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.currentKeeper)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetail?.keeperName ?? '-'}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('manufacturedAt') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.manufacturedAt)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetail?.manufacturedAt ?? '-'}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('expiryDate') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.expiryDate)}
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(ammunitionDetail?.expiryDate, shortDateFormat)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('exhibitNumber') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.exhibitNumber)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetail?.exhibitNumber ?? '-'}
                    </Typography>
                  </Stack>
                </Grid>
              )}
            </AmmunitionInformationContainer>
          </>
        </DetailsTemplate>
      )}

      {componentDetail && (
        <DetailsTemplate title={componentDetail?.type} pageType={EDetailsPageTypes.view}>
          <ComponentInformationContainer size={{ xs: 12 }} container>
            <Grid size={{ xs: 12 }}>
              <Stack>
                <ComponentInformationTitle variant="subtitle1">
                  {t(EComponentLabels.componentGeneralInformation)}
                </ComponentInformationTitle>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.componentType)}
                </Typography>
                <Typography variant="body2">{componentDetail?.type}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography
                  variant="subtitle2"
                  sx={{ marginBottom: '4px', textTransform: 'uppercase' }}
                >
                  {t(EComponentLabels.artId)}
                </Typography>
                <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                  {componentDetail?.artId}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Stack>
                <Link
                  href={`${ERouteLinks.ViewAncillaries.replace(
                    ':id',
                    componentDetail.artUniqueId,
                  ).replace(':type', EArtAncillaryTypeName.COMPONENTS.toLowerCase())}`}
                  underline="none"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    fullWidth
                    label={t('componentDetails.showArtInformation.button')}
                    variant={EButtonVariants.contained}
                    size={EButtonSizes.small}
                  />
                </Link>
              </Stack>
            </Grid>

            {showField('manufacturer') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.manufacturer)}
                  </Typography>
                  <Typography variant="body2">
                    {componentDetail?.manufacturer ?? '-'}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {showField('calibre') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.calibre)}
                  </Typography>
                  <Typography variant="body2">{componentDetail?.calibre ?? '-'}</Typography>
                </Stack>
              </Grid>
            )}
            {showField('composition') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.composition)}
                  </Typography>
                  <Typography variant="body2">
                    {componentDetail?.composition ?? '-'}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {showField('proofHouse') && (
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.proofHouse)}
                  </Typography>
                  <Typography variant="body2">{componentDetail?.proofHouse ?? '-'}</Typography>
                </Stack>
              </Grid>
            )}
          </ComponentInformationContainer>
          <ComponentInformationContainer size={{ xs: 12 }} container borderHidden>
            <Grid size={{ xs: 12 }}>
              <Stack>
                <ComponentInformationTitle variant="subtitle1">
                  {t(EComponentLabels.componentDetailedInformation)}
                </ComponentInformationTitle>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.firecycleID)}
                </Typography>
                <Typography variant="body2">{componentDetail?.fireCycleId ?? '-'}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.serialNumber)}
                </Typography>
                <Typography variant="body2">{componentDetail?.serialNumber ?? '-'}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.componentState)}
                </Typography>
                <Typography variant="body2">
                  <Chips
                    text={emptyFieldHolder(componentDetail?.state)}
                    variant={
                      renderDataColorByValues(
                        componentDetail.activityUniqueId,
                        componentDetail.legalityUniqueId,
                        componentDetail.stateUniqueId,
                      ).stateField
                    }
                  />
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.exhibitNumber)}
                </Typography>
                <Typography variant="body2">
                  {componentDetail?.exhibitNumber ?? '-'}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.currentOwner)}
                </Typography>
                <Typography variant="body2">{componentDetail?.ownerName ?? '-'}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.currentKeeper)}
                </Typography>
                <Typography variant="body2">{componentDetail?.keeperName ?? '-'}</Typography>
              </Stack>
            </Grid>
          </ComponentInformationContainer>
          <ComponentInformationContainer size={{ xs: 12 }} container borderHidden>
            <Grid size={{ xs: 12 }}>
              <Stack>
                <ComponentInformationTitle variant="subtitle1">
                  {t(EComponentLabels.firearmInformation)}
                </ComponentInformationTitle>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.firearmManufacturer)}
                </Typography>
                <Typography variant="body2">
                  {findInDictionary(componentDetail?.frtManufacturerUniqueId, manufacturer)}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.firearmReferenceNo)}
                </Typography>
                <Typography variant="body2">{componentDetail?.frtReferenceNo}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Stack>
                {hasPermissions([EPermission.VIEW_FIREARMS]) && (
                  <Link
                    href={`${ERouteLinks.FRTDetails.replace(':frtId', componentDetail.frtId)}`}
                    underline="none"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant={EButtonVariants.contained}
                      size={EButtonSizes.small}
                      label={t('componentDetails.showFrtInformation.button')}
                      fullWidth
                    />
                  </Link>
                )}
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.firearmFirecycleType)}
                </Typography>
                <Typography variant="body2">
                  {findInDictionary(componentDetail?.frtTypeUniqueId, firearmType)}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.firearmMake)}
                </Typography>
                <Typography variant="body2">
                  {findInDictionary(componentDetail?.frtMakeUniqueId, make)}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(EComponentLabels.firearmModel)}
                </Typography>
                <Typography variant="body2">
                  {findInDictionary(componentDetail?.frtModelUniqueId, model)}
                </Typography>
              </Stack>
            </Grid>
          </ComponentInformationContainer>
        </DetailsTemplate>
      )}

      <Box sx={{ mt: 5 }}>
        <Button
          label={t('general.yes.button')}
          id="yes-button"
          variant={EButtonVariants.contained}
          onClick={() => handleDisable()}
          disabled={disableButtons}
          sx={{
            width: { xs: '100%', sm: '250px' },
            margin: { xs: '0px 0px 20px 0px', sm: '0px 20px 0px 0px' },
          }}
        />
        <Button
          label={t('general.no.button')}
          id="no-button"
          variant={EButtonVariants.outlined}
          disabled={disableButtons}
          onClick={() => navigate(getReturnUrl())}
          sx={{
            width: { xs: '100%', sm: '250px' },
          }}
        />
      </Box>
    </>
  );
};

export default DisableAncillaryPage;
